import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { Card, C, GRAD, Loader, Overline } from "@/components/district/kit";
import GivingPanel from "@/pages/district/GivingPanel";
import { GivingBandsCard } from "@/pages/district/GivingBandsCard";
import { useT } from "@/i18n";
import {
  CheckCircle, Clock, Circle, CaretRight, CalendarBlank, MapTrifold, SquaresFour, Rows,
  GearSix, ClipboardText, MagnifyingGlass,
} from "@phosphor-icons/react";

const LOGO = `${process.env.PUBLIC_URL || ""}/rotary-reverse.png`;
const clubStatus = (c) => (!c.visit ? "not_initiated" : c.visit.status === "closed" ? "completed" : "in_progress");

// Status conveyed by 3 distinct icons + high-contrast text.
const STATUS = {
  completed: { label: "Completed", color: "#0F8A5F", bg: "#E4F7EF", Icon: CheckCircle },
  in_progress: { label: "In progress", color: "#B5730A", bg: "#FEF3E2", Icon: Clock },
  not_initiated: { label: "Not started", color: "#5B5F73", bg: "#EEEFF4", Icon: Circle },
};

// Soft Rotary secondary/tertiary tints — kept clear of the violet hero colour.
const CARD_TINTS = [
  { bg: "#EAF3FB", accent: "#2B77C4" }, // sky blue
  { bg: "#E4F5F6", accent: "#0E8C99" }, // turquoise
  { bg: "#FBE9EF", accent: "#B3175A" }, // cranberry
  { bg: "#FCF3DD", accent: "#B07D10" }, // gold
];

const fmtDate = (iso) => (iso ? formatDate(iso) : null);

// Loader that, after a few seconds, reassures the user a slow fetch is still in flight
// (district dashboard aggregates many collections; first load over a distant DB can take a bit).
function SlowLoader({ label, hint }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const id = setTimeout(() => setSlow(true), 3500); return () => clearTimeout(id); }, []);
  return (
    <div data-testid="district-loading">
      <Loader label={label} />
      {slow && hint && <p className="pl-7 text-[13px]" style={{ color: C.sub }}>{hint}</p>}
    </div>
  );
}


function Person({ label, value, tint }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5">
      <span className="w-9 shrink-0 text-[10px] font-bold uppercase tracking-wide" title={label} style={{ color: tint }}>{label}</span>
      <span className="text-[12px] font-medium leading-snug" style={{ color: value ? C.ink : C.disabled, wordBreak: "break-word" }}>{value || "—"}</span>
    </div>
  );
}

function ClubCard({ c, tint, onOpen }) {
  const s = STATUS[clubStatus(c)];
  const short = (c.club_name || "").replace(/^rotary club of\s*/i, "").replace(/^rotary\s*/i, "");
  const pct = { completed: 100, in_progress: 55, not_initiated: 8 }[clubStatus(c)];
  return (
    <button data-testid={`club-row-${c.club_id}`} onClick={() => onOpen(c)}
      className="flex w-full flex-col rounded-xl p-3 text-left transition-transform duration-150 hover:-translate-y-0.5"
      style={{ background: tint.bg, boxShadow: `inset 0 0 0 1px ${tint.accent}22` }}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-[13px] font-bold leading-tight" style={{ color: C.ink }}>{short || c.club_name}</p>
        <span title={s.label} className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white" style={{ color: s.color }}>
          <s.Icon size={12} weight="fill" />
        </span>
      </div>
      <div className="mt-1.5 space-y-0.5">
        <Person label="Pres" value={c.president} tint={tint.accent} />
        <Person label="Sec" value={c.secretary} tint={tint.accent} />
        <Person label="AG" value={c.ag} tint={tint.accent} />
        <Person label="ZG" value={c.zg} tint={tint.accent} />
        <Person label="ZS" value={c.zs} tint={tint.accent} />
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "#ffffff99" }} title={s.label}>
        <div style={{ width: `${pct}%`, height: "100%", background: s.color }} />
      </div>
    </button>
  );
}

function ZoneSnapshot({ counts, total, label }) {
  const { t } = useT();
  const rows = [
    [t("dist_completed"), counts.completed, STATUS.completed.color],
    [t("dist_in_progress"), counts.in_progress, STATUS.in_progress.color],
    [t("dist_not_started"), counts.not_initiated, STATUS.not_initiated.color],
  ];
  return (
    <Card data-testid="zone-snapshot">
      <Overline>{label}</Overline>
      <p className="mt-3 text-[34px] font-bold leading-none" style={{ color: C.ink }}>
        {counts.completed}<span className="text-[16px] font-semibold" style={{ color: C.sub }}> / {total} {t("dist_clubs_done")}</span>
      </p>
      <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full" style={{ background: "#EEEFF4" }}>
        {rows.map(([, n, col], i) => n > 0 && <div key={i} style={{ width: `${total ? (n / total) * 100 : 0}%`, background: col }} />)}
      </div>
      <div className="mt-4 space-y-2.5">
        {rows.map(([label, n, col]) => (
          <div key={label} className="flex items-center gap-2.5 text-[15px]">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: col }} />
            <span className="flex-1 font-medium" style={{ color: C.sub }}>{label}</span>
            <span className="font-bold" style={{ color: C.ink }}>{n}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RegistrationBar({ reg, notReg }) {
  const { t } = useT();
  const memTotal = (reg || 0) + (notReg || 0);
  if (memTotal === 0) return null;
  const pct = Math.round((reg / memTotal) * 100);
  return (
    <Card data-testid="district-registration" className="p-4 md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
        <div className="shrink-0 md:w-52">
          <Overline>{t("dist_my_rotary_reg")}</Overline>
        </div>
        <div className="flex flex-1 items-center gap-3">
          <div className="flex h-3 flex-1 overflow-hidden rounded-full" style={{ background: "#EEEFF4" }}>
            <div style={{ width: `${pct}%`, background: "#0F8A5F" }} />
          </div>
          <span className="shrink-0 text-[13px] font-bold" style={{ color: C.ink }}>{pct}%</span>
        </div>
        <div className="flex shrink-0 gap-2 text-[13px] font-semibold">
          <span className="rounded-lg px-3 py-1.5 text-center" style={{ background: "#E4F7EF", color: "#0F8A5F" }}>{reg} {t("dist_registered")}</span>
          <span className="rounded-lg px-3 py-1.5 text-center" style={{ background: "#FDECEA", color: "#C8102E" }}>{notReg} {t("dist_not_registered")}</span>
        </div>
      </div>
    </Card>
  );
}

function RegistrationCard({ reg, notReg }) {
  const { t } = useT();
  const memTotal = (reg || 0) + (notReg || 0);
  if (memTotal === 0) return null;
  return (
    <Card data-testid="district-registration">
      <Overline>{t("dist_my_rotary_reg")}</Overline>
      <p className="mt-2 text-[13px] font-medium" style={{ color: C.sub }}>{t("dist_across_district")}</p>
      <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full" style={{ background: "#EEEFF4" }}>
        <div style={{ width: `${(reg / memTotal) * 100}%`, background: "#0F8A5F" }} />
      </div>
      <div className="mt-3 flex gap-2 text-[13px] font-semibold">
        <span className="flex-1 rounded-lg px-2.5 py-1.5 text-center" style={{ background: "#E4F7EF", color: "#0F8A5F" }}>{reg} {t("dist_registered")}</span>
        <span className="flex-1 rounded-lg px-2.5 py-1.5 text-center" style={{ background: "#FDECEA", color: "#C8102E" }}>{notReg} {t("dist_not_registered")}</span>
      </div>
    </Card>
  );
}

function Upcoming({ items, onOpen, cols = 1 }) {
  const { t } = useT();
  return (
    <Card>
      <Overline>{t("dist_upcoming_visits")}</Overline>
      <div className={`mt-3 ${cols === 2 ? "grid grid-cols-1 gap-1.5 sm:grid-cols-2" : "space-y-1"}`}>
        {items.length === 0 && <p className="py-2 text-[15px] font-medium" style={{ color: C.sub }}>{t("dist_no_visits")}</p>}
        {items.map((c) => (
          <button key={c.club_id} data-testid={`upcoming-${c.club_id}`} onClick={() => onOpen(c)}
            className="flex w-full items-center gap-3 rounded-2xl px-2 py-2.5 text-left transition-colors hover:bg-[#F5F3FF]">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: C.indigoSoft, color: C.indigoText }}>
              <CalendarBlank size={19} weight="regular" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold" style={{ color: C.ink }}>{c.club_name}</span>
              <span className="block text-[14px] font-medium" style={{ color: C.sub }}>{fmtDate(c.visit.scheduled_start_utc) || t("dist_date_tbc")}</span>
            </span>
            <CaretRight size={16} weight="bold" style={{ color: C.sub }} />
          </button>
        ))}
      </div>
    </Card>
  );
}

function HeroStat({ value, label }) {
  return (
    <div className="rounded-xl bg-white/12 px-4 py-3 backdrop-blur-sm">
      <p className="text-[32px] font-bold leading-none">{value}</p>
      <p className="mt-2 text-[13px] font-semibold uppercase tracking-wider text-white/75">{label}</p>
    </div>
  );
}

function Hero({ districtNo, name, counts, total, zoneStat }) {
  const { t } = useT();
  return (
    <div className="relative overflow-hidden rounded-2xl px-7 py-8 text-white md:px-9 md:py-9" style={{ background: GRAD.violet }}>
      <div className="pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full" style={{ background: "rgba(255,255,255,0.10)" }} />
      <div className="pointer-events-none absolute -bottom-24 right-24 h-52 w-52 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }} />
      <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <img src={LOGO} alt="Rotary" className="mb-5 h-8 w-auto object-contain" />
          {districtNo && <p className="mb-1 text-[14px] font-bold uppercase tracking-wider text-white/80">District {districtNo}</p>}
          <h1 className="text-[28px] font-bold leading-tight tracking-tight md:text-[34px]">{t("dist_good_day")}, {name}</h1>
          <p className="mt-2 text-[16px] font-medium text-white/85">{t("dist_overview_sub")}</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="hero-stats">
          <HeroStat value={total} label={t("dist_clubs")} />
          <HeroStat value={counts.completed} label={t("dist_completed")} />
          <HeroStat value={counts.in_progress} label={t("dist_in_progress")} />
          <HeroStat value={counts.not_initiated} label={t("dist_not_started")} />
        </div>
      </div>
      {zoneStat && (
        <div className="relative mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/20 pt-4 text-[14px] font-semibold" data-testid="hero-zone-stats">
          <span className="uppercase tracking-wider text-white/80">{zoneStat.label}</span>
          {[[t("dist_completed"), zoneStat.counts.completed, "#8BE8C2"],
            [t("dist_in_progress"), zoneStat.counts.in_progress, "#FBD38D"],
            [t("dist_not_started"), zoneStat.counts.not_initiated, "#D8DAE6"]].map(([label, n, col]) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: col }} />
              <span className="text-white/85">{label}</span>
              <span className="font-bold text-white">{n}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const SUBMISSION = {
  submitted: { label: "Submitted", color: "#0F8A5F", bg: "#E4F7EF" },
  submitted_incomplete: { label: "Submitted · incomplete", color: "#B5730A", bg: "#FEF3E2" },
  not_submitted: { label: "Not submitted", color: "#5B5F73", bg: "#EEEFF4" },
};

function ClubTable({ clubs, onOpen }) {
  const { t } = useT();
  const subLabel = { submitted: t("dist_submitted"), submitted_incomplete: t("dist_submitted_incomplete"), not_submitted: t("dist_not_submitted") };
  return (
    <div className="mt-5 overflow-x-auto" data-testid="clubs-table">
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="text-[12px] font-bold uppercase tracking-wider" style={{ color: C.sub }}>
            <th className="pb-3 pr-3">{t("dist_col_club")}</th>
            <th className="pb-3 px-3">{t("dist_col_zone")}</th>
            <th className="pb-3 px-3">{t("dist_col_gov_date")}</th>
            <th className="pb-3 px-3">{t("dist_col_mock")}</th>
            <th className="pb-3 px-3">{t("dist_col_docs")}</th>
            <th className="pb-3 pl-3">{t("dist_col_submission")}</th>
          </tr>
        </thead>
        <tbody>
          {clubs.map((c) => {
            const short = (c.club_name || "").replace(/^rotary club of\s*/i, "").replace(/^rotary\s*/i, "");
            const sub = SUBMISSION[c.submission] || SUBMISSION.not_submitted;
            const mock = c.mock_gov;
            const pct = c.docs_percent;
            return (
              <tr key={c.club_id} data-testid={`club-tr-${c.club_id}`} onClick={() => onOpen(c)}
                className="cursor-pointer border-t text-[14px] transition-colors hover:bg-[#F5F3FF]" style={{ borderColor: C.line }}>
                <td className="py-3 pr-3 font-semibold" style={{ color: C.ink }}>{short || c.club_name}</td>
                <td className="py-3 px-3" style={{ color: C.sub }}>{c.zone_name || "—"}</td>
                <td className="py-3 px-3" style={{ color: c.visit ? C.ink : C.disabled }}>{(c.visit && fmtDate(c.visit.scheduled_start_utc)) || "—"}</td>
                <td className="py-3 px-3">
                  {mock ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-bold"
                      style={{ background: mock.status === "confirmed" ? "#D6F3E5" : "#FDEBCF", color: mock.status === "confirmed" ? "#0B7A52" : "#9A5B00" }}>
                      {mock.status === "confirmed" ? t("dist_mock_done") : t("dist_mock_proposed")}
                    </span>
                  ) : <span style={{ color: C.disabled }}>—</span>}
                </td>
                <td className="py-3 px-3">
                  {pct == null ? <span style={{ color: C.disabled }}>—</span> : (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-1.5 w-14 overflow-hidden rounded-full" style={{ background: "#EEEFF4" }}>
                        <span className="block h-full" style={{ width: `${pct}%`, background: pct >= 100 ? "#0F8A5F" : "#7C4DFF" }} />
                      </span>
                      <span className="font-semibold" style={{ color: C.ink }}>{pct}%</span>
                    </span>
                  )}
                </td>
                <td className="py-3 pl-3">
                  <span className="inline-flex rounded-full px-2.5 py-1 text-[12px] font-bold" style={{ background: sub.bg, color: sub.color }}>{subLabel[c.submission] || subLabel.not_submitted}</span>
                </td>
              </tr>
            );
          })}
          {clubs.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-center text-[15px] font-medium" style={{ color: C.sub }}>{t("dist_no_clubs")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useT();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [zone, setZone] = useState("__all__");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  const [chosenClub, setChosenClub] = useState(null);
  const [err, setErr] = useState(false);
  const [mobileTab, setMobileTab] = useState("clubs");
  const [visibleCount, setVisibleCount] = useState(20);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useEffect(() => { const z = searchParams.get("zone"); if (z) setZone(z); }, [searchParams]);

  const load = () => api.get("/dashboard/district").then((r) => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);
  useEffect(() => { setVisibleCount(20); }, [zone, q, view]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) setVisibleCount((n) => n + 20); }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [data, zone, q, view, visibleCount]);

  const counts = useMemo(() => {
    const c = { completed: 0, in_progress: 0, not_initiated: 0 };
    (data?.zones || []).forEach((z) => (z.clubs || []).forEach((cl) => { c[clubStatus(cl)]++; }));
    return c;
  }, [data]);

  const upcoming = useMemo(() => {
    const all = [];
    (data?.zones || []).forEach((z) => (z.clubs || []).forEach((cl) => { if (cl.visit && cl.visit.status !== "closed" && cl.visit.scheduled_start_utc) all.push(cl); }));
    return all.sort((a, b) => (a.visit.scheduled_start_utc || "").localeCompare(b.visit.scheduled_start_utc || "")).slice(0, 5);
  }, [data]);

  if (err && !data) return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="district-load-error">
      <p className="text-[16px] font-semibold" style={{ color: C.ink }}>{t("dist_load_failed")}</p>
      <p className="mt-1 text-[14px]" style={{ color: C.sub }}>{t("dist_load_failed_hint")}</p>
      <button data-testid="district-load-retry" onClick={() => { setErr(false); load(); }}
        className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-full px-6 text-[15px] font-medium text-white"
        style={{ background: C.azure }}>{t("retry")}</button>
    </div>
  );
  if (!data) return (
    <div className="space-y-6" data-testid="district-loading-frame">
      {/* Insights rail renders immediately (each panel self-fetches) so the frame is
          interactive within ~2s while the heavier club/zone payload loads. */}
      <div className="grid items-stretch gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="district-insight-cards">
        <GivingPanel part="membership" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
        <GivingPanel part="foundation" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
        <GivingPanel part="recognitions" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
        <GivingPanel part="grants" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
      </div>
      <Card className="p-6"><SlowLoader label={t("dist_loading")} hint={t("dist_loading_hint")} /></Card>
    </div>
  );

  const first = (() => {
    const raw = user?.display_name || "";
    const paren = raw.match(/\(([^)]+)\)/);
    return ((paren ? paren[1] : raw).trim().split(/\s+/)[0]) || "Governor";
  })();
  const zones = data.zones || [];
  const activeZone = zones.find((z) => z.zone_id === zone) || zones[0];
  const allClubs = zones.flatMap((z) => z.clubs || []);
  const totalClubs = counts.completed + counts.in_progress + counts.not_initiated;
  const railClubs = zone === "__all__" ? allClubs : (activeZone?.clubs || []);
  const railCounts = railClubs.reduce((a, c) => { a[clubStatus(c)] += 1; return a; }, { completed: 0, in_progress: 0, not_initiated: 0 });
  const railTotal = railClubs.length;
  const railLabel = activeZone?.zone_name || "Zone";
  const effectiveView = view || "cards";
  const openClub = (c) => setChosenClub(c);
  const canAdmin = !!data.can_admin;

  const ViewToggle = () => (
    <div className="inline-flex items-center gap-0.5" data-testid="view-toggle">
      {[["cards", t("dist_card_view"), SquaresFour], ["table", t("dist_table_view"), Rows]].map(([v, label, Icon]) => (
        <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
          aria-label={label} title={label} aria-pressed={effectiveView === v}
          className="rounded-lg p-1.5 transition-colors"
          style={effectiveView === v
            ? { background: C.indigoSoft, color: C.indigoText }
            : { color: "#A6A7B2" }}>
          <Icon size={17} weight={effectiveView === v ? "fill" : "regular"} />
        </button>
      ))}
    </div>
  );

  const ZoneSelector = () => {
    const tabs = [{ id: "__all__", name: t("dist_all_clubs"), n: allClubs.length },
      ...zones.map((z) => ({ id: z.zone_id, name: z.zone_name, n: (z.clubs || []).length }))];
    if (zones.length > 15) {
      return (
        <div className="mt-3" data-testid="zone-select-wrap">
          <select data-testid="zone-select" value={zone} onChange={(e) => setZone(e.target.value)}
            className="min-h-[46px] w-full rounded-xl border-2 bg-white px-4 text-[15px] font-bold outline-none focus:border-[#7C4DFF]"
            style={{ borderColor: C.line, color: C.indigoText }}>
            {tabs.map((tab) => <option key={tab.id} value={tab.id}>{tab.name} ({tab.n})</option>)}
          </select>
        </div>
      );
    }
    return (
      <div className="mt-3 flex flex-wrap gap-2" data-testid="zone-tabs">
        {tabs.map((tab) => {
          const active = zone === tab.id;
          return (
            <button key={tab.id} data-testid={`zone-tab-${tab.id}`} onClick={() => setZone(tab.id)}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold transition-colors"
              style={active ? { background: C.indigo, color: "#fff" } : { color: C.indigoText, background: "#fff", boxShadow: `inset 0 0 0 1.5px ${C.line}` }}>
              {tab.name}
              <span className="rounded-full px-1.5 text-[11px] font-bold" style={active ? { background: "rgba(255,255,255,0.25)" } : { background: C.indigoSoft, color: C.indigo }}>{tab.n}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Hero districtNo={data.district_no} name={first} counts={counts} total={totalClubs}
        zoneStat={zone !== "__all__" && activeZone ? { label: railLabel, counts: railCounts } : null} />

      {zone === "__all__" ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="district-tools">
          <div className="w-full flex-1 lg:max-w-[680px]">
            <RegistrationBar reg={data.members_registered} notReg={data.members_not_registered} />
          </div>
          {canAdmin && (
            <button data-testid="tool-district-settings" onClick={() => navigate("/setup")}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13.5px] font-bold transition-colors hover:bg-[#F4F5FB]"
              style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line, background: "#fff" }}>
              <GearSix size={16} weight="bold" /> District GOV settings
            </button>
          )}
        </div>
      ) : (canAdmin && (
        <div className="flex flex-wrap items-center justify-end gap-2.5" data-testid="district-tools">
          <button data-testid="tool-district-settings" onClick={() => navigate("/setup")}
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13.5px] font-bold transition-colors hover:bg-[#F4F5FB]"
            style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line, background: "#fff" }}>
            <GearSix size={16} weight="bold" /> District GOV settings
          </button>
        </div>
      ))}

      {zone === "__all__" ? (
        <div className="space-y-4" data-testid="all-zones-insights">
          {/* Find-club search + wrapping zone selector — out of the box (no card) */}
          <div data-testid="find-club-block">
            <div className="relative w-full">
              <MagnifyingGlass size={20} weight="bold" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" style={{ color: C.sub }} />
              <input data-testid="club-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dist_find_club")}
                className="min-h-[50px] w-full rounded-full border-2 pl-12 pr-4 text-[16px] font-medium outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
            </div>
            <ZoneSelector />
            {q.trim() && (() => {
              const qq = q.trim().toLowerCase();
              const matches = allClubs.filter((c) => (c.club_name || "").toLowerCase().includes(qq));
              return (
                <div className="mt-4 border-t pt-4" data-testid="search-results" style={{ borderColor: C.line }}>
                  {matches.length === 0 ? (
                    <p className="py-2 text-[14px] font-medium" style={{ color: C.sub }}>{t("dist_no_clubs")}</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {matches.slice(0, 24).map((c) => {
                        const sn = (c.club_name || "").replace(/^rotary club of\s*/i, "").replace(/^rotary\s*/i, "");
                        return (
                          <button key={c.club_id} data-testid={`search-result-${c.club_id}`} onClick={() => openClub(c)}
                            className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-colors hover:bg-[#F5F3FF]" style={{ borderColor: C.line }}>
                            <span className="min-w-0 truncate text-[14px] font-semibold" style={{ color: C.ink }}>{sn || c.club_name}</span>
                            <span className="shrink-0 text-[12px] font-medium" style={{ color: C.sub }}>{c.zone_name || "—"}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Four insight boxes across the screen */}
          <div className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="district-insight-cards">
            <GivingPanel part="membership" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="foundation" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="recognitions" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="grants" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
          </div>

          {/* Donors by giving band (district-wide) */}
          <GivingBandsCard endpoint="/foundation-giving/district-bands" subtitle="Whole district · by donor band" />

          {/* AG-group box under the four cards */}
          <GivingPanel part="ag" selectedZoneName={null} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
        </div>
      ) : (
        <div className="space-y-4" data-testid="zone-view">
          {/* Control card: zone title + search + zone selector */}
          <Card className="p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-[20px] font-bold tracking-tight" style={{ color: C.ink }}>{activeZone?.zone_name || t("dist_clubs")}</h2>
                <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{(activeZone?.clubs || []).length} {t("dist_clubs_count")}</p>
              </div>
              <div className="relative w-full lg:w-[380px]">
                <MagnifyingGlass size={20} weight="bold" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" style={{ color: C.sub }} />
                <input data-testid="club-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dist_find_club")}
                  className="min-h-[50px] w-full rounded-full border-2 pl-12 pr-4 text-[16px] font-medium outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
              </div>
            </div>
            <ZoneSelector />
          </Card>

          {/* Four insight boxes (zone scope) */}
          <div className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="zone-insight-cards">
            <GivingPanel part="membership" selectedZoneName={activeZone?.zone_name} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="foundation" selectedZoneName={activeZone?.zone_name} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="recognitions" selectedZoneName={activeZone?.zone_name} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
            <GivingPanel part="grants" selectedZoneName={activeZone?.zone_name} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
          </div>

          {/* Club cards box */}
          <Card className="p-4" data-testid="zone-clubs-box">
            {zones.length === 0 ? (
              <p className="py-8 text-center text-[16px] font-medium" style={{ color: C.sub }}>{t("dist_no_zones")}</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[14px]">
                    <span className="text-[16px] font-bold" style={{ color: C.ink }}>{t("dist_clubs")}</span>
                    {(() => {
                      const zg = (activeZone?.clubs || []).map((c) => c.zg).find(Boolean);
                      const zs = (activeZone?.clubs || []).map((c) => c.zs).find(Boolean);
                      const ags = [...new Set((activeZone?.clubs || []).map((c) => c.ag).filter(Boolean))];
                      return (
                        <>
                          {zg && <span style={{ color: C.sub }}>{t("dist_zone_governor")}: <b className="font-semibold" style={{ color: C.ink }}>{zg}</b></span>}
                          {zs && <span style={{ color: C.sub }}>ZS: <b className="font-semibold" style={{ color: C.ink }}>{zs}</b></span>}
                          {ags.length > 0 && <span style={{ color: C.sub }}>{t("dist_ag")}: <b className="font-semibold" style={{ color: C.ink }}>{ags.join(", ")}</b></span>}
                        </>
                      );
                    })()}
                  </div>
                  <ViewToggle />
                </div>
                {(() => {
                  const base = activeZone?.clubs || [];
                  const filtered = q.trim() ? base.filter((c) => (c.club_name || "").toLowerCase().includes(q.trim().toLowerCase())) : base;
                  if (filtered.length === 0) return <p className="mt-5 py-6 text-center text-[16px] font-medium" style={{ color: C.sub }}>{t("dist_no_clubs")}</p>;
                  const shown = filtered.slice(0, visibleCount);
                  const more = visibleCount < filtered.length;
                  const sentinel = more ? <div ref={sentinelRef} data-testid="clubs-load-more-sentinel" className="mt-4 h-8" /> : null;
                  if (effectiveView === "table") return <>{<ClubTable clubs={shown} onOpen={openClub} />}{sentinel}</>;
                  const groups = {};
                  shown.forEach((c) => { (groups[c.ag || "__none__"] ||= []).push(c); });
                  const order = Object.keys(groups).sort((a, b) => (a === "__none__" ? 1 : 0) - (b === "__none__" ? 1 : 0) || a.localeCompare(b));
                  let idx = 0;
                  return (
                    <>
                      {order.map((ag) => (
                        <div key={ag} className="mt-4" data-testid={`ag-group-${ag === "__none__" ? "unassigned" : ag}`}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: C.indigoSoft, color: C.indigoText }}>
                              {t("dist_ag")}: {ag === "__none__" ? t("dist_unassigned_ag") : ag}
                            </span>
                            <span className="text-[12px] font-semibold" style={{ color: C.sub }}>{groups[ag].length} {t("dist_clubs_count")}</span>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            {groups[ag].map((c) => <ClubCard key={c.club_id} c={c} tint={CARD_TINTS[(idx++) % CARD_TINTS.length]} onOpen={openClub} />)}
                          </div>
                        </div>
                      ))}
                      {sentinel}
                    </>
                  );
                })()}
              </>
            )}
          </Card>

          {/* Two tables: CLUB GIVING BY FUND + Recognitions per club */}
          <GivingPanel part="under" selectedZoneName={activeZone?.zone_name} onOpenClub={(r) => navigate(`/review/${r.club_id}`)} />
        </div>
      )}


      {chosenClub && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onClick={() => setChosenClub(null)} data-testid="club-choice-dialog">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: C.sub }}>Open club</p>
            <h3 className="mt-1 text-[20px] font-bold tracking-tight" style={{ color: C.ink }}>{chosenClub.club_name}</h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: C.sub }}>
              Open this club’s Preparation dashboard — the same screen the President uses. You can view and, in support edit mode, make changes on their behalf.
            </p>
            <button data-testid="enter-club-dashboard-btn" onClick={() => navigate(`/club/${chosenClub.club_id}`)}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-[15px] font-bold text-white transition-transform hover:scale-[1.01]"
              style={{ background: GRAD.violet }}>
              <ClipboardText size={18} weight="bold" /> Enter Club Dashboard
            </button>
            <button data-testid="open-gov-review-btn" onClick={() => navigate(`/review/${chosenClub.club_id}`)}
              className="mt-2.5 w-full rounded-full px-4 py-2.5 text-[13.5px] font-bold transition-colors hover:bg-[#F4F5FB]"
              style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line }}>
              Open GOV review instead
            </button>
            <button data-testid="club-choice-cancel" onClick={() => setChosenClub(null)}
              className="mt-2 w-full rounded-full px-4 py-2 text-[13.5px] font-semibold" style={{ color: C.sub }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
